/**
 * Microsoft Clarity loader — heatmaps + session recordings across web + iOS + Android.
 *
 * The native apps are Capacitor WebViews that load this same web bundle, so the
 * single Clarity *web* tag runs on all three surfaces. We deliberately do NOT use
 * Clarity's native iOS/Android SDKs — they instrument native UI, which here is one
 * opaque WebView (no per-element heatmaps), and would force AAB/IPA rebuilds + a
 * custom Capacitor plugin, breaking the Capgo OTA workflow. The web tag captures the
 * real DOM, so heatmaps are per-element accurate, and Clarity auto-tracks SPA route
 * changes via the History API (no manual page-view calls needed).
 *
 * Gated to the PRODUCTION build only so staging, the automated-QA pipeline, and local
 * dev send nothing — keeping heatmaps clean without a second Clarity project or a new
 * CI variable. Every session is tagged with `platform` (web | android | ios) so the
 * three surfaces are filterable in the dashboard — native pages otherwise read as
 * origin `localhost` and blur together with web.
 *
 * The project ID is public (exposed in the client tag regardless), so hardcoding it
 * here is no different from the in-code GA key in public/index.html.
 */
import { getClientPlatform, isNativeApp } from './nativePayment';

const PROJECT_ID = 'x3v1t8d7dr';

let started = false;

/**
 * True only for a deployed PRODUCTION build. We enable Clarity on any deployed
 * https origin that isn't staging and isn't a local dev backend — rather than
 * pinning one exact host — so a future backend-domain change can't silently switch
 * tracking off. A web dev server pointed at a prod backend is still excluded via the
 * web-localhost check (native WebViews are also `localhost` but are real installs, so
 * only the WEB localhost case is skipped). Keyed off `REACT_APP_BACKEND_URL`, which
 * is baked per build for both the web deploy and the native/OTA bundle.
 * @returns {boolean}
 */
function isProdBuild() {
  const be = (process.env.REACT_APP_BACKEND_URL || '').toLowerCase();
  if (!be || be.includes('localhost') || be.includes('127.0.0.1')) return false;
  if (be.includes('staging')) return false;
  if (!be.startsWith('https://')) return false;
  if (typeof window !== 'undefined' && !isNativeApp() &&
      /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)) return false;
  return true;
}

/**
 * Inject the Clarity web tag once and tag the session by platform + environment.
 * No-op off production, on a second call, or in a non-browser (SSR) context. The tag
 * is async, so this never blocks render — fire-and-forget from the App.js root effect.
 */
export function initClarity() {
  if (started || typeof window === 'undefined' || !isProdBuild()) return;
  started = true;

  // Standard Microsoft Clarity bootstrap snippet (async <script> injection).
  (function (c, l, a, r, i, t, y) {
    c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
    t = l.createElement(r); t.async = 1; t.src = 'https://www.clarity.ms/tag/' + i;
    y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
  })(window, document, 'clarity', 'script', PROJECT_ID);

  // Custom tags → filter heatmaps / recordings by surface in the dashboard.
  window.clarity('set', 'platform', getClientPlatform()); // web | android | ios
  window.clarity('set', 'environment', 'production');
}

/**
 * Associate the current Clarity session with a customer so session recordings are
 * tied to a known user. Safe to call before initClarity has run or off prod — it
 * no-ops when the Clarity queue isn't present.
 * @param {string} userId - the app user id (a UUID, not PII).
 */
export function identifyClarityUser(userId) {
  if (typeof window !== 'undefined' && window.clarity && userId) {
    window.clarity('identify', String(userId));
  }
}
